import type { OnDestroy, OnInit } from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';

import { ChangeDetectorRef, Directive, EventEmitter, inject, HostListener, Input, Output } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { Subject, type Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CSS_RESTRICTIONS } from '@common/constants';
import { ThemeStateService } from '@sites/site-theme/src/state';
import { LayoutFacade } from '@web/store/layout/layout.facade';

@Directive()
export abstract class WebBaseControlSettingsComponent<T extends Record<string, any>> implements OnInit, OnDestroy {
    @Input() public item!: T;

    @Output() public cancelEvent: EventEmitter<void> = new EventEmitter();
    @Output() public saveEvent: EventEmitter<T> = new EventEmitter();

    protected readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    protected readonly fb: NonNullableFormBuilder = inject(NonNullableFormBuilder);
    private readonly themeState = inject(ThemeStateService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);

    public readonly CSS_RESTRICTIONS = CSS_RESTRICTIONS;
    protected readonly unsubscribe$ = new Subject<void>();

    protected cachedInitValue = null;
    protected form: UntypedFormGroup;

    protected abstract initForm(): void;
    protected abstract setFormData(): void;

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public readonly isDeviceTablet$: Observable<boolean> = this.layoutFacade.isDeviceTablet$;
    public readonly isDeviceMobile$: Observable<boolean> = this.layoutFacade.isDeviceMobile$;
    public readonly isDeviceDesktop$: Observable<boolean> = this.layoutFacade.isDeviceDesktop$;

    constructor() {
        this.initForm();
    }

    @HostListener('window:keydown.enter')
    public onEnterKeyPress(): void {
        if (this.saveDisablePredicate) {
            return;
        }

        this.onSave();
    }

    /**
     * Panels override this to block Save while their form is invalid — the same
     * hook `WebBaseControlTypedSettingsComponent` exposes. Keep `web-settings-footer`
     * bound to it so the button and the Enter shortcut stay in sync.
     */
    public get saveDisablePredicate(): boolean {
        return false;
    }

    public ngOnInit(): void {
        this.form.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((data) => {
            this.form.markAsDirty({ onlySelf: true });
            this.saveEvent.emit(data as T);
        });

        this.setFormData();

        this.cachedInitValue = this.item;
    }

    public onSave(): void {
        this.form.markAsPristine({ onlySelf: true });
        this.cancelEvent.emit();
    }

    public onReset(): void {
        if (this.form.dirty) {
            this.form.markAsPristine({ onlySelf: true });
            this.saveEvent.emit(this.cachedInitValue);
        }

        this.cancelEvent.emit();
    }

    public onCancel(): void {
        this.onReset();
    }

    public ngOnDestroy(): void {
        this.onReset();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
